import type { VistaCard } from "../contratos";
import { lazy, Suspense, useRef, useState, type KeyboardEvent } from "react";

import { Avisos, Blocos, type NomeBloco } from "../card/Card";
import { FORMATOS, formato, useFormatos, type IdFormato } from "./formatos";
import estilos from "./mesa.module.css";

// O card vai no pacote principal (é o que mais chega por link); os outros formatos carregam
// sob demanda, para a primeira carga caber no orçamento do ADR 0010.
const Didatico = lazy(() => import("../formatos/Didatico"));
const Numeros = lazy(() => import("../formatos/Numeros"));
const Encaminhar = lazy(() => import("../formatos/Encaminhar"));
const Imagem = lazy(() => import("../formatos/Imagem"));

const ABAS = FORMATOS.filter((f) => !f.emBreve);

/** Formatos que cabem nesta vista e neste canal. */
export function formatosDisponiveis(vista: VistaCard, imagemUrl: string | null): IdFormato[] {
  const lista: IdFormato[] = ["card", "didatico"];
  if (vista.dado && vista.dado.serie.length > 0) lista.push("numeros");
  lista.push("encaminhar");
  if (imagemUrl) lista.push("imagem");
  return lista;
}

/** Estado neutro do resultado, nunca um rótulo de veredito (AGENTS.md). */
export const rotuloEstado = (vista: VistaCard) =>
  vista.sem_conclusao ? "Sem conclusão" : (vista.guia ?? "Analisada");

/**
 * O resultado no fio: a alegação neutra, o estado e as abas de formatos (padrão ARIA de
 * tablist). No card, cada bloco ganha um botão "Evidência →" que abre a fonte no inspetor;
 * os blocos são os componentes fixos de `src/card` (invariante 5).
 */
export function Documento(props: {
  vista: VistaCard;
  textoUrl: string;
  imagemUrl: string | null;
  recarregar: () => void;
  escolhido: NomeBloco | null;
  aoEscolher: (bloco: NomeBloco | null) => void;
}) {
  const { vista } = props;
  const disponiveis = formatosDisponiveis(vista, props.imagemUrl);
  const { ativo, abrir } = useFormatos(disponiveis);
  const abas = useRef<HTMLDivElement>(null);
  const [copiado, setCopiado] = useState(false);

  const teclar = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = disponiveis.indexOf(ativo);
    const n = disponiveis.length;
    const destino =
      e.key === "ArrowRight"
        ? disponiveis[(i + 1) % n]
        : e.key === "ArrowLeft"
          ? disponiveis[(i - 1 + n) % n]
          : e.key === "Home"
            ? disponiveis[0]
            : e.key === "End"
              ? disponiveis[n - 1]
              : undefined;
    if (!destino) return;
    e.preventDefault();
    abrir(destino);
    abas.current?.querySelector<HTMLButtonElement>(`#aba-${destino}`)?.focus();
  };

  return (
    <article aria-labelledby="alegacao">
      {/* Cabeçalho fixo: a alegação, o estado e as visualizações ficam à mão ao rolar. */}
      <header className={estilos.cabecalho}>
        <div className={estilos.cabecalhoLinha}>
          <div className={estilos.cabecalhoTitulos}>
            <p className={estilos.rotulo}>
              Afirmação analisada, em forma neutra · {vista.data} · versão {vista.versao}
            </p>
            <h1 id="alegacao" tabIndex={-1}>
              {vista.alegacao}
            </h1>
            <p className={estilos.estado}>
              <span className={estilos.ponto} data-sem={vista.sem_conclusao} aria-hidden="true" />
              {rotuloEstado(vista)}
            </p>
          </div>
          <button
            type="button"
            className={estilos.copiar}
            aria-label={copiado ? "Link copiado" : "Copiar link"}
            onClick={() => {
              void navigator.clipboard.writeText(location.href).then(() => {
                setCopiado(true);
              });
            }}
          >
            <span aria-hidden="true">{copiado ? "✓" : "⧉"}</span>
            <span className={estilos.copiarRotulo}>{copiado ? "Copiado" : "Copiar link"}</span>
          </button>
        </div>
        {/* O teclado (setas) é tratado no tablist, como pede o padrão ARIA. */}
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          ref={abas}
          role="tablist"
          aria-label="Formatos"
          className={estilos.abas}
          onKeyDown={teclar}
        >
          {ABAS.map((f) => {
            const pode = disponiveis.includes(f.id);
            return (
              <button
                key={f.id}
                id={`aba-${f.id}`}
                type="button"
                role="tab"
                aria-selected={f.id === ativo}
                aria-controls="painel-formato"
                tabIndex={f.id === ativo ? 0 : -1}
                disabled={!pode}
                title={pode ? f.descricao : "Este formato não se aplica a esta análise."}
                onClick={() => {
                  abrir(f.id);
                }}
              >
                <span aria-hidden="true" className={estilos.abaIcone}>
                  {f.icone}
                </span>
                <span className={estilos.abaNome}>{f.nome}</span>
              </button>
            );
          })}
        </div>
        <p className={estilos.abaDica}>{formato(ativo).descricao}</p>
      </header>
      <Avisos vista={vista} />
      <div id="painel-formato" role="tabpanel" aria-labelledby={`aba-${ativo}`}>
        <Suspense fallback={<p role="status">Montando o formato…</p>}>
          {ativo === "card" && (
            <Blocos
              vista={vista}
              textoUrl={props.textoUrl}
              recarregar={props.recarregar}
              envolver={(nome, bloco) => (
                <div key={nome} className={estilos.bloco} data-escolhido={props.escolhido === nome}>
                  {bloco}
                  {/* "Sobre" já é a ficha da análise: não tem evidência própria a abrir. */}
                  {nome !== "Sobre" && (
                    <button
                      type="button"
                      className={estilos.evidencia}
                      aria-pressed={props.escolhido === nome}
                      onClick={() => {
                        props.aoEscolher(props.escolhido === nome ? null : nome);
                      }}
                    >
                      Evidência<span className="visualmente-oculto">: {nome}</span>{" "}
                      <span aria-hidden="true">→</span>
                    </button>
                  )}
                </div>
              )}
            />
          )}
          {ativo === "didatico" && <Didatico vista={vista} />}
          {ativo === "numeros" && <Numeros vista={vista} />}
          {ativo === "encaminhar" && <Encaminhar vista={vista} textoUrl={props.textoUrl} />}
          {ativo === "imagem" && props.imagemUrl && (
            <Imagem vista={vista} imagemUrl={props.imagemUrl} />
          )}
        </Suspense>
      </div>
    </article>
  );
}
